import {
  IBitrixDeal,
  IBitrixDealPlacement,
  IBitrixDealsInDealPlacement,
  IBitrixDealVirtual,
} from 'src/store/crosstable/reducer';
import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import {
  canWrite, isFullyBlocked, writeBlockReason, writeBlockReasons,
} from 'src/accessControl';

export const useBitrixDealHook = (deal: IBitrixDeal | IBitrixDealsInDealPlacement | null | undefined, selectedPlacement: IBitrixDealPlacement | undefined) => {
  const [undoProgress, setUndoProgress] = useState<number>(0);
  const getTimeAgoContent = useCallback((virtual?: IBitrixDealVirtual) => {
    if (!virtual?.lastPlacementLink?.at) return undefined;
    return moment(virtual.lastPlacementLink.at).fromNow();
  }, []);

  const getLinkedDateContent = useCallback((virtual?: IBitrixDealVirtual) => {
    if (!virtual?.lastPlacementLink?.at) return undefined;
    return moment(virtual.lastPlacementLink.at).format('D MMM, YYYY • HH:mm');
  }, []);

  const placementId = typeof deal?.placement === 'string'
    ? deal?.placement
    : deal?.placement?.id;
  const isLinkedUnit = useCallback((_deal: any) => !!_deal?.placement, []);

  const isUnitCanBeLinked = useMemo<boolean>(() => {
    if (!deal || deal.placement) return false;
    // Attaching is a null->value write on the deal's `placement` and an append
    // on the unit's `bitrixDeals`; a missing rule entry means "no restrictions".
    return canWrite(deal['@accessRules'], 'placement', 'null_to_value_blocked')
      && canWrite(selectedPlacement?.['@accessRules'], 'bitrixDeals', 'append_blocked');
  }, [selectedPlacement, deal]);

  const isUnitCannotBeLinkedByStatus = useMemo<boolean>(() => {
    return !!deal?.id
      && !canWrite(selectedPlacement?.['@accessRules'], 'bitrixDeals', 'append_blocked');
  }, [selectedPlacement, deal]);

  // The deal is FREE (no placement) but attaching is blocked — two flavors,
  // keyed by the reason type of the null_to_value blocker:
  //  - user_isnt_responsible -> "Another user is responsible for this deal…";
  //  - any other reason (policy) -> "Linking units to deals isn't enabled."
  // A full lock (all_blocked) is excluded — the Access restricted case owns it.
  const linkingBlockReasons = useMemo(() => {
    if (!deal || deal.placement) return [];
    if (isFullyBlocked(deal['@accessRules'], 'placement')) return [];
    return writeBlockReasons(deal['@accessRules'], 'placement', 'null_to_value_blocked');
  }, [deal]);
  const isLinkingNotEnabledForUser = linkingBlockReasons.length > 0;
  const isLinkingBlockedByResponsibility = linkingBlockReasons
    .some((reason) => reason.type === 'user_isnt_responsible');

  // "#N is linked to this deal. Your current permissions do not allow you…" —
  // the deal's placement carries a value_to_null blocker (but not a full lock).
  // Reasons ACCUMULATE (user_isnt_responsible and/or unlink_disabled_by_policy):
  // any of them forbids the unlink, so the flag keys off the blocked action —
  // unlink_disabled_by_policy blocks even when status/placement checks pass.
  const isBitrixDealUnlinkBlocked = useMemo<boolean>(() => {
    return !canWrite(deal?.['@accessRules'], 'placement', 'value_to_null_blocked')
      && !isFullyBlocked(deal?.['@accessRules'], 'placement');
  }, [deal]);

  // all_blocked is NEVER a business rule for `placement` (the backend
  // decomposes responsibility into the scalar actions) — a full lock means
  // fail-closed `access_restricted` or the legacy `denied` fallback. The UI
  // shows a neutral technical notice, not a business explanation.
  const isBitrixDealAccessRestricted = useMemo<boolean>(() => {
    return isFullyBlocked(deal?.['@accessRules'], 'placement');
  }, [deal]);

  // Canonical backend message of the standalone all_blocked blocker
  // ("Access restricted." / "Access denied."); any action matches it.
  const bitrixDealAccessRestrictedMessage = useMemo<string | null>(() => {
    if (!isBitrixDealAccessRestricted) return null;
    return writeBlockReason(deal?.['@accessRules'], 'placement', 'value_to_null_blocked');
  }, [deal, isBitrixDealAccessRestricted]);

  const isDealHasCurrentUnit = useMemo<boolean>(() => {
    return !!placementId && placementId === selectedPlacement?.id;
  }, [placementId, selectedPlacement]);

  const openCurrentDealLink = (link?: string) => {
    window.open(link, '_blank');
  };

  return {
    isLinkedUnit,
    isUnitCanBeLinked,
    isUnitCannotBeLinkedByStatus,
    isLinkingNotEnabledForUser,
    isLinkingBlockedByResponsibility,
    isBitrixDealAccessRestricted,
    bitrixDealAccessRestrictedMessage,
    isBitrixDealUnlinkBlocked,
    isDealHasCurrentUnit,
    undoProgress,
    getTimeAgoContent,
    getLinkedDateContent,
    setUndoProgress,
    openCurrentDealLink,
  };
};
